import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useTypesenseProxyContext,
  ProxyCollection,
  ProxySearchParams,
} from "contexts/TypesenseProxyContext";
import { debounce } from "Utils";

export const useTypesenseDocuments = ({
  documentIds,
  collection,
  ...params
}: {
  documentIds?: string[];
  collection: ProxyCollection;
} & Partial<ProxySearchParams>) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [found, setFound] = useState(0);

  const { proxySearch } = useTypesenseProxyContext();

  const memoizedSearchParams: ProxySearchParams = useMemo(
    () => ({
      q: params.q || "*",
      query_by: params.query_by || "",
      limit: params.limit,
      offset: params.offset,
      sort_by: params.sort_by,
      filter_by: params.filter_by,
      page: params.page,
      per_page: params.per_page,
    }),
    [
      params.q,
      params.limit,
      params.offset,
      params.query_by,
      params.sort_by,
      params.filter_by,
      params.page,
      params.per_page,
    ],
  );

  async function getDocuments(searchParameters: ProxySearchParams) {
    setError("");
    try {
      if (documentIds) {
        searchParameters.filter_by = [
          searchParameters.filter_by,
          `id:=[${documentIds.join(",")}]`,
        ]
          .filter((filter) => !!filter)
          .join(" && ");
      }

      const sanitizedSearchParameters = Object.fromEntries(
        Object.entries(searchParameters).filter(([, v]) => v !== undefined),
      ) as ProxySearchParams;
      const response = await proxySearch(collection, sanitizedSearchParameters);
      setDocuments(response.hits?.map((hit) => hit.document) ?? []);
      setFound(response.found);
    } catch (error) {
      console.error(error);
      setDocuments([]);
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(debounce(getDocuments), []);

  function forceReload() {
    if (proxySearch) getDocuments(memoizedSearchParams);
  }

  useEffect(() => {
    if (proxySearch) {
      // set the loader here on params changes to get immediate feedback even when debounced
      setLoading(true);
      debouncedQuery(memoizedSearchParams);
    }
  }, [proxySearch, memoizedSearchParams, debouncedQuery]);

  return { documents, forceReload, loading, error, found };
};
